/**
 * "Civil" (calendar) dates: values that mean a day, not an instant.
 *
 * The API stores some of them as date-only strings ("2026-01-01") and some as
 * UTC midnight timestamps ("2026-01-01T00:00:00.000Z", e.g. statement-created
 * expenses and statement dates). Feeding those to `new Date()` / the date pipe
 * in Eastern time shows the previous day (12/31/2025). These helpers keep the
 * calendar day; real timestamps (with a time of day) are left alone.
 */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const UTC_MIDNIGHT = /^(\d{4})-(\d{2})-(\d{2})T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)$/;

/** Returns a local Date on the intended calendar day, or null if empty/invalid. */
export function toCivilDate(value: any): Date | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const m = value.match(DATE_ONLY) || value.match(UTC_MIDNIGHT);
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** Year and 0-based month of a civil date (timestamps are read in Eastern time). */
export function civilYearMonth(value: any): { year: number; month: number } | null {
  if (typeof value === 'string') {
    const m = value.match(DATE_ONLY) || value.match(UTC_MIDNIGHT);
    if (m) {
      return { year: Number(m[1]), month: Number(m[2]) - 1 };
    }
  }
  const d = toCivilDate(value);
  if (!d) {
    return null;
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'numeric'
  }).formatToParts(d);
  const year = Number((parts.find(p => p.type === 'year') || { value: 'NaN' }).value);
  const month = Number((parts.find(p => p.type === 'month') || { value: 'NaN' }).value) - 1;
  return { year, month };
}

/** YYYY-MM-DD for a local Date (no UTC shift). */
export function localIsoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
